import {AsyncThunk, createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import type {PayloadAction} from "@reduxjs/toolkit";
import {IPromptResultRecord} from "common";
import AIAPIUtility from "../../classes/ai/AIAPI";

// First, create the thunk
export const fetchPromptCompletions: AsyncThunk<IPromptResultRecord[], void, any> = createAsyncThunk<IPromptResultRecord[]>(
	'slice/fetchPromptCompletions',
	async () => await AIAPIUtility.listPromptCompletions()
)

export const deletePromptCompletion: AsyncThunk<IPromptResultRecord, string, any> = createAsyncThunk(
	'slice/deletePromptCompletion',
	async (id: string) => await AIAPIUtility.deletePromptCompletion(id)
)

export const ADD_MESSAGE = "ADD_MESSAGE";
export const addMessage = (message) => ({
	type: ADD_MESSAGE,
	payload: message
});

export interface IChatGPTState {
	prompt: string;
	savedPrompts: IPromptResultRecord[];
	savedPromptLoadingState: string;
	promptEntered: string | null;
	focusPrompt: boolean | undefined;
	code: string;
	result: string;
	sending: boolean;
	statusText: string;
	commandHistory: string[];
	maxTokens: number;
	sessionId: string;
}

const initialState: IChatGPTState = {
	prompt: '',
	savedPrompts: [],
	savedPromptLoadingState: "",
	promptEntered: null,
	focusPrompt: undefined,
	code: "",
	result: "",
	sending: false,
	statusText: "",
	commandHistory: [],
	maxTokens: 256,
	sessionId: "",
};

const ChatGPTSlice = createSlice({
	name: "chatgpt",
	initialState,
	reducers: {
		setPrompt(state, action: PayloadAction<string>)
		{
			state.prompt = action.payload;
		},
		setSavedPrompts(state, action: PayloadAction<IPromptResultRecord[]>)
		{
			state.savedPrompts = action.payload;
		},
		setPromptEntered(state, action: PayloadAction<string>)
		{
			state.promptEntered = action.payload;
		},
		setCode(state, action: PayloadAction<string>)
		{
			state.code = action.payload;
		},
		setResult(state, action: PayloadAction<string>)
		{
			state.result = action.payload;
		},
		setSending(state, action: PayloadAction<boolean>)
		{
			state.sending = action.payload;
		},
		setCommandHistory(state, action: PayloadAction<string[]>)
		{
			state.commandHistory = action.payload;
		},
		setMaxTokens(state, action: PayloadAction<number>)
		{
			state.maxTokens = action.payload;
		},
		setStatusText(state, action: PayloadAction<string>)
		{
			state.statusText = action.payload;
		},
		focusPrompt(state, action: PayloadAction<boolean | undefined>)
		{
			state.focusPrompt = action.payload;
		},
		setSessionId(state, action: PayloadAction<string>)
		{
			state.sessionId = action.payload;
		}
	},
	extraReducers: (builder) =>
	{
		builder
			.addCase(fetchPromptCompletions.pending, (state) =>
			{
				state.savedPromptLoadingState = 'loading';
			})
			.addCase(fetchPromptCompletions.fulfilled, (state, action: PayloadAction<IPromptResultRecord[]>) =>
			{
				state.savedPromptLoadingState = 'succeeded';
				// Add the fetched data to the state
				state.savedPrompts = action.payload;
			})
			.addCase(fetchPromptCompletions.rejected, (state, action) =>
			{
				state.savedPromptLoadingState = (action.error as Error)?.message || 'failed';
			});
	},
});

export const {
	focusPrompt
} = ChatGPTSlice.actions;

export default ChatGPTSlice;